import React from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Button,
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material'
import {
  ArrowBack,
  CameraAlt,
  PhotoLibrary,
  Mic,
  Edit,
  CheckCircle,
  Science,
  Speed,
  Security,
} from '@mui/icons-material'

const AIDataEntry: React.FC = () => {
  const navigate = useNavigate()

  const inputMethods = [
    {
      icon: <CameraAlt fontSize="large" color="primary" />,
      title: 'Camera Input',
      description: 'Take photos of medical documents for instant AI analysis',
      features: [
        'Snap pictures of doctor\'s notes and prescriptions',
        'Photograph lab results and medical reports',
        'Capture medication labels for automatic tracking',
        'Instant processing with AI extraction',
      ]
    },
    {
      icon: <PhotoLibrary fontSize="large" color="primary" />,
      title: 'Photo Library',
      description: 'Upload existing images from your device',
      features: [
        'Select multiple photos at once',
        'Access your entire photo library',
        'Upload historical medical documents',
        'Batch process multiple images',
      ]
    },
    {
      icon: <Mic fontSize="large" color="primary" />,
      title: 'Voice Input',
      description: 'Speak your medical history naturally',
      features: [
        'Speech-to-text conversion',
        'Describe symptoms in your own words',
        'Hands-free data entry',
        'Natural language processing',
      ]
    },
    {
      icon: <Edit fontSize="large" color="primary" />,
      title: 'Text Entry',
      description: 'Type your health information directly',
      features: [
        'Direct text input for precise entry',
        'Edit and refine your information',
        'Copy and paste from other sources',
        'Full control over your data',
      ]
    },
  ]

  const benefits = [
    {
      icon: <Speed />,
      title: 'Lightning Fast',
      description: 'AI processes your data in seconds, extracting key medical information automatically'
    },
    {
      icon: <Science />,
      title: 'AI-Powered',
      description: 'Advanced OpenAI technology understands medical terminology and context'
    },
    {
      icon: <Security />,
      title: 'Privacy First',
      description: 'Your data is encrypted and you control what information to share'
    },
  ]

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: 4 }}>
      <Container maxWidth="lg">
        <Button 
          startIcon={<ArrowBack />}
          onClick={() => navigate('/')}
          sx={{ mb: 3 }}
        >
          Back to Home
        </Button>

        <Paper sx={{ p: 4, mb: 4 }}>
          <Box textAlign="center" mb={6}>
            <Typography variant="h2" fontWeight="bold" gutterBottom>
              AI-Powered Data Entry
            </Typography>
            <Typography variant="h5" color="text.secondary">
              Multiple ways to share your health information - fast, easy, and intelligent
            </Typography>
          </Box>

          {/* Hero Section */}
          <Box sx={{ bgcolor: 'primary.main', color: 'white', p: 4, borderRadius: 3, mb: 6 }}>
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={6}>
                <Typography variant="h4" fontWeight="bold" gutterBottom>
                  Choose Your Preferred Method
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Our AI understands medical information no matter how you input it. Take a photo, 
                  use your voice, upload images, or type directly - the choice is yours.
                </Typography>
                <Button 
                  variant="contained" 
                  size="large"
                  sx={{ bgcolor: 'white', color: 'primary.main', '&:hover': { bgcolor: 'grey.100' } }}
                  onClick={() => navigate('/register')}
                >
                  Get Started Free
                </Button>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ bgcolor: 'rgba(255,255,255,0.1)', p: 3, borderRadius: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    What Makes It Special?
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemIcon sx={{ color: 'white' }}>
                        <CheckCircle />
                      </ListItemIcon>
                      <ListItemText primary="AI extracts medical information automatically" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon sx={{ color: 'white' }}>
                        <CheckCircle />
                      </ListItemIcon>
                      <ListItemText primary="Understands medical terminology and abbreviations" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon sx={{ color: 'white' }}>
                        <CheckCircle />
                      </ListItemIcon>
                      <ListItemText primary="Organizes data into structured health profiles" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon sx={{ color: 'white' }}>
                        <CheckCircle />
                      </ListItemIcon>
                      <ListItemText primary="Works with handwritten notes and printed documents" />
                    </ListItem>
                  </List>
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Input Methods */}
          <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ mt: 6, mb: 3 }}>
            Four Powerful Input Methods
          </Typography>
          <Grid container spacing={3} sx={{ mb: 6 }}>
            {inputMethods.map((method, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card sx={{ height: '100%', '&:hover': { boxShadow: 6 } }}>
                  <CardContent>
                    <Box display="flex" alignItems="center" gap={2} mb={2}>
                      {method.icon}
                      <Typography variant="h5" fontWeight="bold">
                        {method.title}
                      </Typography>
                    </Box>
                    <Typography variant="body1" color="text.secondary" paragraph>
                      {method.description}
                    </Typography>
                    <Divider sx={{ my: 2 }} />
                    <List dense>
                      {method.features.map((feature, idx) => (
                        <ListItem key={idx}>
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <CheckCircle color="success" fontSize="small" />
                          </ListItemIcon>
                          <ListItemText primary={feature} />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Benefits */}
          <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ mt: 6, mb: 3 }}>
            Why AI-Powered Entry?
          </Typography>
          <Grid container spacing={3}>
            {benefits.map((benefit, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card sx={{ textAlign: 'center', p: 3, height: '100%' }}>
                  <Box sx={{ color: 'primary.main', mb: 2 }}>
                    {React.cloneElement(benefit.icon, { sx: { fontSize: 48 } })}
                  </Box>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    {benefit.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {benefit.description}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* How It Works */}
          <Box sx={{ mt: 6, bgcolor: 'grey.50', p: 4, borderRadius: 3 }}>
            <Typography variant="h4" fontWeight="bold" gutterBottom textAlign="center">
              How It Works
            </Typography>
            <Grid container spacing={3} sx={{ mt: 2 }}>
              <Grid item xs={12} md={3}>
                <Box textAlign="center">
                  <Box sx={{ 
                    width: 60, 
                    height: 60, 
                    borderRadius: '50%', 
                    bgcolor: 'primary.main', 
                    color: 'white', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    fontSize: '1.5rem', 
                    fontWeight: 'bold',
                    mx: 'auto',
                    mb: 2
                  }}>
                    1
                  </Box>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Choose Input Method
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Select camera, photo, voice, or text input
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={3}>
                <Box textAlign="center">
                  <Box sx={{ 
                    width: 60, 
                    height: 60, 
                    borderRadius: '50%', 
                    bgcolor: 'primary.main', 
                    color: 'white', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    fontSize: '1.5rem', 
                    fontWeight: 'bold',
                    mx: 'auto',
                    mb: 2
                  }}>
                    2
                  </Box>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Share Your Data
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Take photo, speak, or type your medical info
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={3}>
                <Box textAlign="center">
                  <Box sx={{ 
                    width: 60, 
                    height: 60, 
                    borderRadius: '50%', 
                    bgcolor: 'primary.main', 
                    color: 'white', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    fontSize: '1.5rem', 
                    fontWeight: 'bold',
                    mx: 'auto',
                    mb: 2
                  }}>
                    3
                  </Box>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    AI Processes
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Advanced AI extracts and categorizes information
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={3}>
                <Box textAlign="center">
                  <Box sx={{ 
                    width: 60, 
                    height: 60, 
                    borderRadius: '50%', 
                    bgcolor: 'primary.main', 
                    color: 'white', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    fontSize: '1.5rem', 
                    fontWeight: 'bold',
                    mx: 'auto',
                    mb: 2
                  }}>
                    4
                  </Box>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Review & Confirm
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Check organized data and start matching trials
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* CTA */}
          <Box textAlign="center" sx={{ mt: 6 }}>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              Ready to Try AI-Powered Data Entry?
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Start entering your health data the easy way
            </Typography>
            <Button 
              variant="contained" 
              size="large"
              onClick={() => navigate('/register')}
              sx={{ px: 6, py: 2 }}
            >
              Get Started Free
            </Button>
          </Box>
        </Paper>
      </Container>
    </Box>
  )
}

export default AIDataEntry

